<template>
  <div
    class="poll-results"
    :class="{ 'poll-results--live': isActive }"
  >
    <div class="poll-results__header">
      <v-icon
        x-small
        color="primary"
        class="mr-1"
      >
        mdi-chart-bar
      </v-icon>
      <span class="poll-results__question">{{ poll.question }}</span>
      <span
        class="poll-results__badge"
        :class="{ 'poll-results__badge--live': isActive }"
      >
        {{ isActive ? 'Live' : 'Closed' }}
      </span>
    </div>
    <div class="poll-results__body">
      <div
        v-if="!results || results.length === 0"
        class="poll-results__empty"
      >
        No votes recorded.
      </div>
      <div
        v-for="result in results"
        :key="result.option_id"
        class="poll-results__row"
      >
        <div class="poll-results__label">
          <span>{{ result.option_text }}</span>
          <span class="poll-results__count">{{ result.count }} vote{{ result.count === 1 ? '' : 's' }}</span>
        </div>
        <div class="poll-results__bar-wrap">
          <div class="poll-results__bar-bg">
            <div
              class="poll-results__bar-fill"
              :style="{ width: result.percentage + '%' }"
            />
          </div>
          <span class="poll-results__pct">{{ result.percentage }}%</span>
        </div>

        <!-- Other texts (if applicable) -->
        <div
          v-if="result.is_other && result.other_texts && result.other_texts.length > 0"
          class="poll-results__other-texts"
        >
          <span
            v-for="(text, idx) in result.other_texts"
            :key="idx"
            class="poll-results__other-text"
          >
            "{{ text }}"
          </span>
        </div>
      </div>
      <div
        v-if="showClose"
        class="poll-results__close-row"
      >
        <button
          class="poll-results__close-btn"
          :disabled="closing"
          @click="$emit('close')"
        >
          <v-icon
            x-small
            class="mr-1"
          >
            mdi-close-circle-outline
          </v-icon>
          {{ closing ? 'Closing...' : 'Close Poll' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PollResultsCard',

  props: {
    poll: {
      type: Object,
      required: true,
    },
    results: {
      type: Array,
      default: () => [],
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    closing: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
.poll-results {
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1.5px solid rgba(244, 119, 33, 0.2);
  background: #fff;
  overflow: hidden;

  &--live {
    border-color: rgba(67, 160, 71, 0.35);
    background: rgba(67, 160, 71, 0.02);
  }

  &__header {
    display: flex;
    align-items: flex-start;
    padding: 10px 12px 6px;
    gap: 2px;
  }

  &__question {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
    line-height: 1.35;
  }

  &__badge {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.45);
    line-height: 1.3;
    margin-left: 6px;

    &--live {
      background: #43a047;
      color: #fff;
    }
  }

  &__body {
    padding: 0 12px 10px;
  }

  &__empty {
    color: rgba(0, 0, 0, 0.35);
    font-size: 12px;
    padding: 4px 0;
  }

  &__row {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__label {
    font-size: 12px;
    margin-bottom: 3px;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    color: rgba(0, 0, 0, 0.7);
  }

  &__count {
    color: rgba(0, 0, 0, 0.4);
    font-size: 10px;
    white-space: nowrap;
    margin-left: 8px;
  }

  &__bar-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__bar-bg {
    flex: 1;
    height: 10px;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  &__bar-fill {
    height: 100%;
    border-radius: 5px;
    background: #f47721;
    transition: width 0.3s ease;
    min-width: 0;
  }

  &__pct {
    font-size: 10px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    min-width: 28px;
    text-align: right;
  }

  &__other-texts {
    margin-top: 4px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.55);
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__other-text {
    background: rgba(244, 119, 33, 0.06);
    border-radius: 4px;
    padding: 1px 6px;
    font-style: italic;
  }

  &__close-row {
    display: flex;
    justify-content: flex-end;
    padding: 4px 12px 8px;
  }

  &__close-btn {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border: none;
    border-radius: 5px;
    background: transparent;
    color: #c62828;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(198, 40, 40, 0.08);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}
</style>
